/**
 * NotificationSettingsPage - Email Notification Preferences
 * 
 * User preferences for email notifications.
 * Only includes backed features with confirmed database columns and email dispatch.
 * 
 * SCOPE: v0.9.5 - Email notifications only (backed)
 * DEFERRED: Push notifications (v1.0.0), SMS (v1.2.0)
 */

import { useNavigate } from 'react-router-dom';
import { TopBar, SettingsSection, Toggle, Skeleton } from '../../components';
import { useNotificationPreferences, useUpdateNotificationPreferences } from '../../hooks/useNotifications';
import { Bell } from 'lucide-react';
import type { UpdateNotificationPreferencesRequest } from '../../hooks/useNotifications';

export default function NotificationSettingsPage() {
  const navigate = useNavigate();
  const { data, isLoading } = useNotificationPreferences();
  const updateMutation = useUpdateNotificationPreferences();

  const preferences = data?.preferences;

  const handleToggle = async (key: keyof UpdateNotificationPreferencesRequest, value: boolean) => {
    try {
      await updateMutation.mutateAsync({ [key]: value });
    } catch (error) {
      console.error('Failed to update notification preference:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-oro-bg">
        <TopBar title="Notifications" onBack={() => navigate('/settings')} />
        <div className="max-w-2xl mx-auto p-4 space-y-6">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      </div>
    );
  }

  if (!preferences) {
    return (
      <div className="min-h-screen bg-oro-bg">
        <TopBar title="Notifications" onBack={() => navigate('/settings')} />
        <div className="max-w-2xl mx-auto p-4">
          <p className="text-center text-text-secondary">Unable to load notification preferences</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-oro-bg">
      <TopBar title="Notifications" onBack={() => navigate('/settings')} />
      
      <div className="max-w-2xl mx-auto p-4 space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 bg-oro-surface-alt rounded-full flex items-center justify-center">
            <Bell className="w-5 h-5 text-accent" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-text-primary">Notifications</h1>
            <p className="text-sm text-text-secondary">Manage how ORO keeps you informed</p>
          </div>
        </div>

        {/* Email Notifications Section */}
        <SettingsSection
          title="Email Notifications"
          description="Control when ORO sends you emails"
        >
          <Toggle
            label="Event Reminders"
            description="Get reminded about events you've RSVP'd to"
            checked={preferences.email_event_reminders}
            onChange={(checked) => handleToggle('email_event_reminders', checked)}
            disabled={updateMutation.isPending}
          />
          
          <Toggle
            label="RSVP Confirmations"
            description="Receive confirmation when you RSVP to an event"
            checked={preferences.email_rsvp_confirmations}
            onChange={(checked) => handleToggle('email_rsvp_confirmations', checked)}
            disabled={updateMutation.isPending}
          />
          
          <Toggle
            label="Event Updates"
            description="Notifications when event details change"
            checked={preferences.email_event_updates}
            onChange={(checked) => handleToggle('email_event_updates', checked)}
            disabled={updateMutation.isPending}
          />
          
          <Toggle
            label="Club Updates"
            description="Important announcements from your clubs"
            checked={preferences.email_club_updates}
            onChange={(checked) => handleToggle('email_club_updates', checked)}
            disabled={updateMutation.isPending}
          />
          
          <Toggle
            label="Club Invitations"
            description="When someone invites you to join a club"
            checked={preferences.email_invites}
            onChange={(checked) => handleToggle('email_invites', checked)}
            disabled={updateMutation.isPending}
          />
          
          <Toggle
            label="Weekly Digest"
            description="Summary of your week's activities and upcoming events"
            checked={preferences.email_digest}
            onChange={(checked) => handleToggle('email_digest', checked)}
            disabled={updateMutation.isPending}
          />
        </SettingsSection>

        {/* Info Box */}
        <div className="bg-oro-surface-alt border border-oro-border rounded-xl p-4">
          <p className="text-sm text-text-secondary">
            <span className="font-medium text-text-primary">About Email Notifications:</span> You can 
            unsubscribe from any email type at any time. Critical security notifications will always be sent 
            regardless of your preferences.
          </p>
        </div>
      </div>
    </div>
  );
}
